import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../lib/auth.tsx'
import { Button } from './ui.tsx'
import {
  IconAnalytics, IconAudit, IconBilling, IconClose, IconLogout, IconMenu,
  IconPatients, IconProducts, IconRx, IconUsers,
} from './icons.tsx'

const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?'

export function AppShell() {
  const { me, signOut, can, busy } = useAuth()

  // On a phone the sidebar is a drawer rather than a column. It is closed by
  // default, because the screen belongs to the record being read, not to the
  // menu.
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  // Tapping a section closes the drawer. Without this the menu stays over
  // the page it just navigated to, which reads as the tap not having worked.
  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  // Escape closes it, like every other dismissible layer in the app.
  useEffect(() => {
    if (!menuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [menuOpen])

  if (!me) return null

  // The menu is built from the permissions the SERVER sent, so it can never
  // offer something the API would then refuse. This is tidiness, not
  // security -- every route is enforced again server-side.
  const links = [
    { to: '/patients', label: 'Patients', icon: IconPatients, show: can('patient:read') },
    { to: '/prescriptions', label: 'Prescriptions', icon: IconRx, show: can('prescription:read') },
    { to: '/billing', label: 'Billing', icon: IconBilling, show: can('billing:read') },
    { to: '/products', label: 'Products', icon: IconProducts, show: can('product:read') },
    { to: '/analytics', label: 'Analysis', icon: IconAnalytics, show: can('analytics:read') },
    { to: '/staff', label: 'Staff', icon: IconUsers, show: can('staff:read') },
    { to: '/audit', label: 'Audit log', icon: IconAudit, show: can('audit:read') },
  ].filter(l => l.show)

  return (
    <div className={`shell${menuOpen ? ' menu-open' : ''}`}>
      {/* Phone only: the bar that holds the menu button. On a desktop the
          sidebar is always there and this does not exist. */}
      <header className="mobile-bar">
        <button
          type="button" className="burger" onClick={() => setMenuOpen(true)}
          aria-label="Open menu" aria-expanded={menuOpen} aria-controls="app-nav"
        >
          <IconMenu />
        </button>
        <span className="mark">RP</span>
        <div className="truncate mobile-title">Rajasthan Pharmacia</div>
      </header>

      <div className="scrim" onClick={() => setMenuOpen(false)} aria-hidden />

      <aside className="sidebar" id="app-nav">
        <div className="brand">
          <span className="mark">RP</span>
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="name truncate">Rajasthan Pharmacia</div>
            <div className="sub">Jodhpur</div>
          </div>
          <button
            type="button" className="burger close-menu" onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <IconClose size={16} />
          </button>
        </div>

        <nav className="nav" aria-label="Sections">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => (isActive ? 'active' : '')}>
              <Icon className="ic" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="who">
          <span className="av">{initials(me.staff.fullName)}</span>
          <div className="grow" style={{ lineHeight: 1.3, minWidth: 0 }}>
            <div className="truncate" style={{ fontWeight: 550 }}>{me.staff.fullName}</div>
            <div className="truncate dim" style={{ fontSize: 'var(--text-xs)' }}>{me.staff.email}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={signOut} loading={busy} aria-label="Sign out" title="Sign out">
            <IconLogout />
          </Button>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>
    </div>
  )
}

export function PageHeader({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <header className="topbar">
      <h1 className="grow truncate">{title}</h1>
      {children}
    </header>
  )
}
